import { guestCount, groupSizeBucket } from './discover-pricing.mjs';

/**
 * Header Search Component
 * Handles the main navigation search form including:
 * - Type selection
 * - Google Places autocomplete
 * - Cities dropdown
 * - Clean URL generation
 */

(function () {
  const headerSearchForm = document.getElementById('header-search-form');
  if (!headerSearchForm) return;

  // Get Google Maps API key from data attribute
  const googleMapsApiKey = headerSearchForm.dataset.googleMapsApiKey;

  // DOM elements
  const typeSelect = headerSearchForm.querySelector('[data-header-search-target="type"]');
  const keywordInput = headerSearchForm.querySelector('[data-header-search-target="keyword"]');
  const addressField = headerSearchForm.querySelector('[data-header-search-address]');
  const addressInput = headerSearchForm.querySelector('[data-header-search-target="address"]');
  const citiesDropdown = document.getElementById('header-cities-dropdown');
  const cityLocationField = document.getElementById('header-city-location');
  const searchFlag = document.getElementById('header-search-flag');

  // State
  let showingCitiesDropdown = false;
  let selectedCitySlug = null;
  let headerAutocompleteService;
  let headerPlacesService;
  let headerPlacesScriptPromise;
  let headerPlacesScriptLoaded = false;

  // Hidden field IDs for location data
  const hiddenFieldIds = [
    'header-latitude',
    'header-longitude',
    'header-location-name',
    'header-city',
    'header-state',
    'header-country'
  ];

  /**
   * Clear all location-related form fields
   */
  function clearLocationFields() {
    hiddenFieldIds.forEach(function (id) {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
    if (addressInput) addressInput.value = '';
    if (cityLocationField) cityLocationField.value = '';
    if (searchFlag) searchFlag.value = '';
    selectedCitySlug = null;
  }

  /**
   * Clear hidden location fields without clearing the visible address input.
   * Called when the user starts typing to remove stale values from a prior search.
   */
  function clearHiddenLocationFields() {
    hiddenFieldIds.forEach(function (id) {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
    if (cityLocationField) cityLocationField.value = '';
    if (searchFlag) searchFlag.value = '';
    selectedCitySlug = null;
  }

  /**
   * Show/hide location fields based on experience type
   * @param {string} typeValue - The selected experience type
   */
  function toggleLocationFields(typeValue) {
    // Location applies to In-Person and All Experiences; only Virtual hides it.
    const showLocation = typeValue !== 'Virtual';
    if (addressField) addressField.hidden = !showLocation;
    if (addressInput) addressInput.disabled = !showLocation;
    if (!showLocation) {
      clearLocationFields();
      hideCitiesDropdown();
    }
  }

  /**
   * Show the cities dropdown
   */
  function showCitiesDropdown() {
    if (citiesDropdown) {
      citiesDropdown.style.display = 'block';
      showingCitiesDropdown = true;
    }
  }

  /**
   * Hide the cities dropdown
   */
  function hideCitiesDropdown() {
    if (citiesDropdown) {
      citiesDropdown.style.display = 'none';
      showingCitiesDropdown = false;
    }
  }

  /**
   * Filter cities dropdown items by query, showing dropdown if any match
   * @param {string} query
   */
  function filterCitiesDropdown(query) {
    if (!citiesDropdown) return;
    const items = citiesDropdown.querySelectorAll('#header-cities-list .hero-cities-dropdown__item');
    const normalised = query.trim().toLowerCase();
    let anyVisible = false;
    items.forEach(function(item) {
      const label = item.querySelector('span').textContent.toLowerCase();
      const matches = !normalised || label.indexOf(normalised) >= 0;
      item.style.display = matches ? '' : 'none';
      if (matches) anyVisible = true;
    });
    const citiesHeader = document.getElementById('header-cities-header');
    if (citiesHeader) citiesHeader.style.display = anyVisible ? '' : 'none';
    updateDropdownVisibility();
  }

  /**
   * Show or hide the combined dropdown based on whether any section has results
   */
  function updateDropdownVisibility() {
    const citiesHeader = document.getElementById('header-cities-header');
    const hasVisibleCities = citiesHeader && citiesHeader.style.display !== 'none';
    const googleSuggestions = document.getElementById('header-google-suggestions');
    const hasGoogleResults = googleSuggestions && googleSuggestions.children.length > 0;
    if (hasVisibleCities || hasGoogleResults) {
      showCitiesDropdown();
    } else {
      hideCitiesDropdown();
    }
  }

  /**
   * Load Google Places API script
   * @returns {Promise}
   */
  function loadHeaderPlacesScript() {
    if (headerPlacesScriptLoaded) return Promise.resolve();
    if (headerPlacesScriptPromise) return headerPlacesScriptPromise;

    headerPlacesScriptPromise = new Promise(function (resolve, reject) {
      if (window.google && window.google.maps && window.google.maps.places) {
        headerPlacesScriptLoaded = true;
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&callback=initHeaderPlaces`;
      script.async = true;
      script.defer = true;
      script.onerror = function () { reject(new Error('Failed to load Google Maps')); };
      window.initHeaderPlaces = function () {
        headerPlacesScriptLoaded = true;
        resolve();
      };
      document.head.appendChild(script);
    });

    return headerPlacesScriptPromise;
  }

  /**
   * Initialize Google Places services (AutocompleteService + PlacesService)
   */
  function initHeaderPlacesServices() {
    if (headerAutocompleteService) return;
    if (!(window.google && google.maps && google.maps.places)) return;
    headerAutocompleteService = new google.maps.places.AutocompleteService();
    const serviceEl = document.createElement('div');
    headerPlacesService = new google.maps.places.PlacesService(serviceEl);
  }

  const pinSvgHeader = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:1rem;height:1rem;flex-shrink:0;"><path d="M12 21s6-5.4 6-10a6 6 0 1 0-12 0c0 4.6 6 10 6 10Z"/><circle cx="12" cy="11" r="2.6" fill="currentColor" stroke="none"/></svg>';

  /**
   * Fetch Google Places predictions and render into the combined dropdown
   * @param {string} query
   */
  function fetchHeaderGoogleSuggestions(query) {
    const googleSuggestions = document.getElementById('header-google-suggestions');
    const googleHeader = document.getElementById('header-google-header');
    if (!headerAutocompleteService || !query || query.trim().length < 2) {
      if (googleSuggestions) googleSuggestions.innerHTML = '';
      if (googleHeader) googleHeader.style.display = 'none';
      updateDropdownVisibility();
      return;
    }
    // Passive "Searching…" row while Google responds; the callback replaces it.
    if (googleSuggestions && !googleSuggestions.children.length) {
      if (googleHeader) googleHeader.style.display = '';
      googleSuggestions.innerHTML =
        '<div class="hero-cities-dropdown__item" aria-hidden="true" style="opacity:0.55;cursor:default;">' +
        '<span class="ds-btn-spinner"></span><span>Searching…</span></div>';
      updateDropdownVisibility();
    }
    headerAutocompleteService.getPlacePredictions(
      { input: query, types: ['geocode', 'establishment'] },
      function(predictions, status) {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length) {
          renderHeaderGoogleSuggestions(predictions.slice(0, 5));
        } else {
          if (googleSuggestions) googleSuggestions.innerHTML = '';
          if (googleHeader) googleHeader.style.display = 'none';
          updateDropdownVisibility();
        }
      }
    );
  }

  /**
   * Render Google prediction items into the combined dropdown
   * @param {Array} predictions
   */
  function renderHeaderGoogleSuggestions(predictions) {
    const container = document.getElementById('header-google-suggestions');
    const header = document.getElementById('header-google-header');
    if (!container) return;
    container.innerHTML = '';
    if (!predictions || !predictions.length) {
      if (header) header.style.display = 'none';
      updateDropdownVisibility();
      return;
    }
    if (header) header.style.display = '';
    const supportedCountries = ['usa', 'united states', 'canada'];
    predictions.forEach(function(prediction) {
      const fmt = prediction.structured_formatting || {};
      const mainText = fmt.main_text || prediction.description;
      const secondaryText = fmt.secondary_text || '';
      const terms = prediction.terms || [];
      const lastTerm = terms.length ? terms[terms.length - 1].value.toLowerCase() : '';
      const isSupported = !lastTerm || supportedCountries.indexOf(lastTerm) >= 0;
      const item = document.createElement('div');
      item.className = 'hero-cities-dropdown__item';
      const label = isSupported ? mainText : 'Coming Soon: ' + mainText;
      item.innerHTML = pinSvgHeader + '<span>' + label + (secondaryText ? '<small style="opacity:0.55;margin-left:4px;font-size:0.8em;">' + secondaryText + '</small>' : '') + '</span>';
      if (!isSupported) {
        item.style.opacity = '0.45';
        item.style.cursor = 'not-allowed';
      } else {
        item.addEventListener('mousedown', function(e) {
          e.preventDefault();
          if (addressInput) addressInput.value = mainText;
          hideCitiesDropdown();
          if (headerPlacesService) {
            headerPlacesService.getDetails(
              { placeId: prediction.place_id, fields: ['name', 'geometry', 'address_components', 'formatted_address', 'types'] },
              function(place, detailStatus) {
                if (detailStatus === google.maps.places.PlacesServiceStatus.OK && place) {
                  setHeaderPlace(place);
                }
              }
            );
          }
        });
      }
      container.appendChild(item);
    });
    updateDropdownVisibility();
  }

  /**
   * Set form fields based on selected Google Place
   * @param {Object} place - Google Place object
   */
  function setHeaderPlace(place) {
    let city = '';
    let state = '';
    let country = '';
    let street = '';
    let streetNumber = '';

    (place.address_components || []).forEach(function (component) {
      if (component.types.indexOf('locality') >= 0) city = component.long_name;
      if (component.types.indexOf('administrative_area_level_1') >= 0) state = component.short_name;
      if (component.types.indexOf('country') >= 0) country = component.long_name;
      if (component.types.indexOf('route') >= 0) street = component.long_name;
      if (component.types.indexOf('street_number') >= 0) streetNumber = component.long_name;
    });

    let locationName = '';
    const streetAddress = [streetNumber, street].filter(Boolean).join(' ').trim();
    const placeName = place.name || '';
    
    // Only set location_name if it's a specific venue/landmark (not just a city)
    // Check if the place is an establishment/point_of_interest or has a street address
    const isEstablishment = place.types && (
      place.types.indexOf('establishment') >= 0 ||
      place.types.indexOf('point_of_interest') >= 0 ||
      place.types.indexOf('premise') >= 0
    );
    
    if (streetAddress) {
      // For street addresses, use the street address
      locationName = streetAddress;
    } else if (isEstablishment && placeName && placeName !== city) {
      // For landmarks/venues that aren't the same as the city name
      locationName = placeName;
    }
    // Otherwise leave locationName empty (city-level search)

    const latField = document.getElementById('header-latitude');
    const lngField = document.getElementById('header-longitude');

    if (latField && place.geometry?.location?.lat) latField.value = place.geometry.location.lat();
    if (lngField && place.geometry?.location?.lng) lngField.value = place.geometry.location.lng();

    const locField = document.getElementById('header-location-name');
    if (locField) locField.value = locationName;

    const cityFieldInput = document.getElementById('header-city');
    const stateField = document.getElementById('header-state');
    const countryField = document.getElementById('header-country');

    if (cityFieldInput) cityFieldInput.value = city;
    if (stateField) stateField.value = state;
    if (countryField) countryField.value = country;
    
    if (cityLocationField) cityLocationField.value = '';
    selectedCitySlug = null;

    if (searchFlag) searchFlag.value = 'true';
    checkAutoSubmit();
  }

  /**
   * Initialize form state from URL parameters
   */
  function initializeFormState() {
    if (cityLocationField && cityLocationField.value) {
      const cityItems = citiesDropdown ? citiesDropdown.querySelectorAll('.hero-cities-dropdown__item') : [];
      cityItems.forEach(function(item) {
        if (item.getAttribute('data-city') === cityLocationField.value) {
          selectedCitySlug = item.getAttribute('data-slug');
        }
      });
    }
  }
  
  initializeFormState();

  function checkAutoSubmit() {
    window.location.href = buildCleanUrl();
  }

  // Event Listeners

  // Type select change
  if (typeSelect) {
    toggleLocationFields(typeSelect.value);
    typeSelect.addEventListener('change', function (event) {
      toggleLocationFields(event.target.value);
      checkAutoSubmit();
    });
  }
  
  // Address input events
  if (addressInput) {
    addressInput.addEventListener('focus', function () {
      filterCitiesDropdown(addressInput.value);
      loadHeaderPlacesScript().then(initHeaderPlacesServices).catch(function () {});
    });

    addressInput.addEventListener('input', function () {
      clearHiddenLocationFields();
      filterCitiesDropdown(addressInput.value);
      if (addressInput.value.trim().length >= 2) {
        loadHeaderPlacesScript().then(function() {
          initHeaderPlacesServices();
          fetchHeaderGoogleSuggestions(addressInput.value);
        }).catch(function () {});
      } else {
        const googleSuggestions = document.getElementById('header-google-suggestions');
        const googleHeader = document.getElementById('header-google-header');
        if (googleSuggestions) googleSuggestions.innerHTML = '';
        if (googleHeader) googleHeader.style.display = 'none';
        updateDropdownVisibility();
      }
    });
    
    addressInput.addEventListener('blur', function (e) {
      setTimeout(function() {
        if (!document.activeElement || !document.activeElement.closest('[data-header-location-wrapper]')) {
          hideCitiesDropdown();
        }
      }, 200);
    });
    
  }
  
  // Cities dropdown click handlers
  if (citiesDropdown) {
    const cityItems = citiesDropdown.querySelectorAll('.hero-cities-dropdown__item');
    cityItems.forEach(function(item) {
      item.addEventListener('click', function() {
        const cityValue = item.getAttribute('data-city');
        const cityLabel = item.querySelector('span').textContent;
        const citySlug = item.getAttribute('data-slug');
        
        if (addressInput) addressInput.value = cityLabel;
        if (cityLocationField) cityLocationField.value = cityValue;
        if (searchFlag) searchFlag.value = '';
        
        selectedCitySlug = citySlug;
        
        const latField = document.getElementById('header-latitude');
        const lngField = document.getElementById('header-longitude');
        const locField = document.getElementById('header-location-name');
        if (latField) latField.value = '';
        if (lngField) lngField.value = '';
        if (locField) locField.value = '';
        
        hideCitiesDropdown();
        checkAutoSubmit();
      });
    });
  }
  
  // Close dropdown when clicking outside
  document.addEventListener('click', function(e) {
    if (citiesDropdown && 
        !e.target.closest('[data-header-location-wrapper]') && 
        !e.target.closest('.hero-cities-dropdown')) {
      hideCitiesDropdown();
    }
  });

  // Form submit handler
  if (headerSearchForm) {
    headerSearchForm.addEventListener('submit', function (e) {
      e.preventDefault();
      
      if (typeSelect && typeSelect.value === 'Virtual') {
        clearLocationFields();
      }

      const selectedType = typeSelect ? typeSelect.value : '';
      const hasLatLng = document.getElementById('header-latitude') && 
                     document.getElementById('header-latitude').value;
      const hasCityLocation = cityLocationField && cityLocationField.value;
      const inputValue = addressInput ? addressInput.value.trim() : '';

      // If typed location text hasn't been resolved yet (In-Person or All
      // Experiences — Virtual has no location field), geocode before navigating
      if (selectedType !== 'Virtual' && inputValue && !hasLatLng && !selectedCitySlug) {
        loadHeaderPlacesScript().then(function() {
          initHeaderPlacesServices();
          if (!headerAutocompleteService) {
            window.location.href = buildCleanUrl();
            return;
          }
          headerAutocompleteService.getPlacePredictions(
            { input: inputValue, types: ['geocode', 'establishment'] },
            function(predictions, status) {
              if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length && headerPlacesService) {
                headerPlacesService.getDetails(
                  { placeId: predictions[0].place_id, fields: ['name', 'geometry', 'address_components', 'formatted_address', 'types'] },
                  function(place, detailStatus) {
                    if (detailStatus === google.maps.places.PlacesServiceStatus.OK && place) {
                      setHeaderPlace(place);
                    }
                    window.location.href = buildCleanUrl();
                  }
                );
              } else {
                window.location.href = buildCleanUrl();
              }
            }
          );
        }).catch(function() {
          window.location.href = buildCleanUrl();
        });
        return;
      }

      if (addressInput && addressInput.value.trim()) {
        if (hasLatLng) {
          if (searchFlag) searchFlag.value = 'true';
        } else if (hasCityLocation) {
          if (searchFlag) searchFlag.value = '';
        }
      } else {
        if (searchFlag) searchFlag.value = '';
      }
      
      const cleanUrl = buildCleanUrl();
      window.location.href = cleanUrl;
    });
  }

  /**
   * Append Discover's search fields to a vanity URL. FormData branches already
   * include these controls; this covers the string-literal routes.
   * @param {string} url
   * @returns {string}
   */
  function withKeyword(url) {
    const target = new URL(url, window.location.origin);
    const keyword = keywordInput ? keywordInput.value.trim() : '';
    if (keyword) target.searchParams.set('q', keyword);
    const guests = guestCount(headerSearchForm.elements.guests?.value);
    if (guests) {
      target.searchParams.set('guests', guests);
      target.searchParams.set('group_size', groupSizeBucket(guests));
    }
    ['price_min', 'price_max', 'duration', 'platform_location'].forEach(name => {
      const value = headerSearchForm.elements[name]?.value.trim();
      if (value) target.searchParams.set(name, value);
    });
    ['venue_type', 'insurance_coverage'].forEach(name => {
      const control = headerSearchForm.elements[name];
      if (control?.checked) target.searchParams.set(name, control.value);
    });
    return target.pathname + target.search;
  }

  /**
   * Build clean URL based on form state
   * @returns {string} The clean URL to navigate to
   */
  function buildCleanUrl() {
    // Portal search stays on the portal: keep keyword/location as query params
    // on the portal path instead of routing to the /activities vanity URLs.
    // `type` is excluded — it only exists to enable the location field UI.
    const portalPath = headerSearchForm.dataset.portalPath;
    if (portalPath) {
      const portalFormData = new FormData(headerSearchForm);
      const portalParams = new URLSearchParams();
      for (const pair of portalFormData.entries()) {
        if (pair[0] === 'type') continue;
        if (pair[1] && pair[1].trim() !== '') {
          portalParams.append(pair[0], pair[1]);
        }
      }
      const portalQuery = portalParams.toString();
      return portalPath + (portalQuery ? '?' + portalQuery : '');
    }

    const selectedType = typeSelect ? typeSelect.value : '';
    const hasLatLng = document.getElementById('header-latitude') && 
                   document.getElementById('header-latitude').value;
    
    // If using lat/lng (Google Places search), use query params
    if (hasLatLng) {
      const formData = new FormData(headerSearchForm);
      const params = new URLSearchParams();

      for (const pair of formData.entries()) {
        if (pair[0] === 'type' && pair[1] === 'All Experiences') {
          // A resolved location implies in-person; the /activities?type=In-Person
          // route redirect canonicalizes this to /activities/in-person.
          params.append('type', 'In-Person');
          continue;
        }
        if (pair[1] && pair[1].trim() !== '') {
          params.append(pair[0], pair[1]);
        }
      }

      return '/activities?' + params.toString();
    }
    // If using city slug (from dropdown), use vanity URL
    else if (selectedCitySlug) {
      return withKeyword('/activities/' + selectedCitySlug);
    }
    // Virtual experiences
    else if (selectedType === 'Virtual') {
      return withKeyword('/activities/virtual');
    }
    // In-Person experiences (no specific location)
    else if (selectedType === 'In-Person') {
      return withKeyword('/activities/in-person');
    }
    // All other cases
    else {
      if (selectedType === 'All Experiences') {
        return withKeyword('/activities');
      } else {
        const formData = new FormData(headerSearchForm);
        const params = new URLSearchParams();

        for (const pair of formData.entries()) {
          if (pair[0] === 'type' && pair[1] === 'All Experiences') {
            continue;
          }
          if (pair[1] && pair[1].trim() !== '') {
            params.append(pair[0], pair[1]);
          }
        }

        return '/activities?' + params.toString();
      }
    }
  }
})();
